/**
 * @description
 * Utility class for setting, getting, and erasing cookies.
 */
export class CookieUtils {
  /**
   * @description
   * Set a cookie with the given key and value. If expiry is set, the cookie will
   * expire after the given number of days. If domain is set, the cookie will be
   * set on that domain.
   *
   * @param {string} key - cookie key to set with a value (e.g. 'consentMode')
   * @param {Object} value - object to be set as cookie value
   * @param {number} expiry - number of days until cookie expires
   * @param {string} domain - domain to set cookie on (e.g. '.respondent.io')
   *
   * @example
   * Prefix domain with a dot to set cookie on the domain and all subdomains:
   * setCookie('consentMode', {...}, 365 * 100, '.respondent.io');
   */
  static setCookie(key, value, expiry, domain) {
    let expires = '';
    if (expiry && typeof expiry === 'number') {
      const date = new Date();
      // Convert days to milliseconds
      date.setTime(date.getTime() + expiry * 24 * 60 * 60 * 1000);
      expires = `; expires=${date.toUTCString()}`;
    }
    const domainValue = domain ? `; domain=${domain}` : '';

    document.cookie = `${key}=${value}${expires}${domainValue}; path=/`;
  }

  /**
   * @description
   * Set a encoded cookie with the given key and value. If expiry is set, the cookie
   * will expire after the given number of days. If domain is set, the cookie will be
   * set on that domain.
   *
   * @param {string} key - cookie key to set with a value (e.g. 'consentMode')
   * @param {Object} value - object to be serialized and set as cookie value
   * @param {number} expiry - number of days until cookie expires
   * @param {string} domain - domain to set cookie on (e.g. '.respondent.io')
   *
   * @example
   * Prefix domain with a dot to set cookie on the domain and all subdomains:
   * setEncodedCookie('consentMode', {...}, 365 * 100, '.respondent.io');
   */
  static setEncodedCookie(key, value, expiry, domain) {
    try {
      const encodedCookieValue = encodeURIComponent(JSON.stringify(value));
      this.setCookie(key, encodedCookieValue, expiry, domain);
    } catch (error) {
      console.error(`Error encoding the value for cookie '${key}':`, error);
    }
  }

  /**
   * @description
   * Get a cookie value by key.
   *
   * @param {string} key - cookie key to get value for (e.g. 'consentMode')
   * @returns {Object | null} - cookie value or null if not found
   */
  static getCookie(key) {
    const allCookies = document.cookie.split('; ');
    const targetCookie = allCookies.find((cookie) => {
      const [cookieKey] = cookie.split('=');
      return cookieKey === key;
    });

    return targetCookie ? targetCookie.split('=')[1] : null;
  }

  /**
   * @description
   * Get an encoded cookie value by key and return
   * the decoded value.
   *
   * @param {string} key - cookie key to get value for (e.g. 'consentMode')
   * @returns {Object | null} - deserialized cookie value or null if not found
   */
  static getEncodedCookie(key) {
    const cookieValue = this.getCookie(key);

    if (cookieValue) {
      try {
        return JSON.parse(decodeURIComponent(cookieValue));
      } catch (error) {
        console.error(`Error encoding the value for cookie '${key}':`, error);
      }
    }

    return null;
  }

  /**
   * @description
   * Erase a cookie by key.
   *
   * @param {string} key - cookie key to erase (e.g. 'consentMode')
   * @param {string} domain - domain to erase cookie from (e.g. '.respondent.io')
   *
   * @example
   * Prefix domain with a dot if used when setting the cookie:
   * eraseCookie('consentMode', '.respondent.io');
   */
  static eraseCookie(key, domain) {
    const domainString = domain ? ` domain=${domain};` : '';
    document.cookie = `${key}=; Max-Age=-99999999;${domainString}`;
  }
}
